import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getAllPhotos } from "../data/photos";
import Lightbox from "../components/Lightbox";

const PHOTOS = getAllPhotos();

const FILTERS = [
  { key: "all",          label: "Todas" },
  { key: "portraits",    label: "Retratos" },
  { key: "street",       label: "Calle" },
  { key: "architecture", label: "Edificios" },
];

export default function Gallery() {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("cat") || "all";

  const filtered = useMemo(() => {
    if (filter === "all") return PHOTOS;
    return PHOTOS.filter((p) => p.category === filter);
  }, [filter]);

  const setFilter = (k) => {
    if (k === "all") {
      searchParams.delete("cat");
    } else {
      searchParams.set("cat", k);
    }
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div data-testid="gallery-page" className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <header className="grid grid-cols-12 gap-6 mb-16 reveal">
          <div className="col-span-12 md:col-span-7">
            <p className="eyebrow mb-4">Archivo</p>
            <h1 className="font-serif-display text-paper text-5xl sm:text-6xl leading-[0.95] tracking-tight">
              Galería<br />
              <span className="italic text-paper-dim">completa.</span>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-5 flex md:justify-end items-end">
            <p className="text-paper-dim font-light max-w-sm">
              Una selección curada en tres capítulos. Toca cualquier imagen para verla a tamaño completo.
            </p>
          </div>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap gap-8 border-b border-ink-700 mb-10" data-testid="gallery-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`chip ${filter === f.key ? "active" : ""}`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
              <span className="ml-2 text-paper-mute">
                {f.key === "all"
                  ? PHOTOS.length
                  : PHOTOS.filter((p) => p.category === f.key).length}
              </span>
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="py-32 text-center" data-testid="gallery-empty">
            <p className="font-serif-display text-paper text-3xl">Aún no hay fotos en esta serie.</p>
            <p className="mt-3 text-paper-dim font-light">Vuelve pronto. Hay luz en camino.</p>
          </div>
        ) : (
          <div className="masonry columns-1 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
            {filtered.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setLightboxIdx(i)}
                className="photo-card group w-full text-left"
                data-testid={`photo-card-${p.id}`}
              >
                <img src={p.image_data} alt={p.title} loading="lazy" />
                <div className="meta">
                  <div className="font-serif-display text-paper text-lg leading-tight">{p.title}</div>
                  <div className="mt-1 eyebrow">{labelOf(p.category)}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {lightboxIdx !== null && filtered[lightboxIdx] && (
          <Lightbox
            photos={filtered}
            index={lightboxIdx}
            onClose={() => setLightboxIdx(null)}
            onPrev={() => setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length)}
            onNext={() => setLightboxIdx((i) => (i + 1) % filtered.length)}
          />
        )}
      </div>
    </div>
  );
}

function labelOf(c) {
  return c === "portraits" ? "Retratos" : c === "street" ? "Calle" : "Edificios";
}
